import { useEffect, useState } from 'react';
import { Mark } from './Mark';

/**
 * Mark + wordmark, horizontal only (spec §1).
 *
 * Proportions are derived from mark height rather than hardcoded, so the lockup
 * holds at any size:
 *   gap          = 0.29 x mark height
 *   clear space  = 11/48 of mark height (the offset between the rects)
 *
 * Below a 320px viewport the wordmark drops and the mark stands alone.
 */
interface LockupProps {
  /** Mark height in px; everything else scales from it. */
  size?: number;
  monochrome?: boolean;
  className?: string;
}

const NARROW_VIEWPORT = '(max-width: 319px)';

export function Lockup({ size = 32, monochrome = false, className }: LockupProps) {
  const [markOnly, setMarkOnly] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(NARROW_VIEWPORT);
    const sync = () => setMarkOnly(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  const gap = size * 0.29;
  const clearSpace = size * (11 / 48);

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: `${gap}px`,
        padding: `${clearSpace}px`,
        // The lower rect's left edge aligns to the container gutter. The rect starts
        // at x=9 on a 48 grid, so pull the optical inset back out rather than
        // re-centring the mark, which §1 forbids.
        marginInlineStart: `${-(size * (9 / 48)) - clearSpace}px`,
      }}
    >
      {/* Decorative when the wordmark is present: the text already names the brand. */}
      <Mark size={size} monochrome={monochrome} decorative={!markOnly} />

      {!markOnly && (
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: `${size * 0.72}px`,
            letterSpacing: '-0.008em',
            lineHeight: 1,
            color: monochrome ? 'currentColor' : 'var(--color-brand-ink)',
            whiteSpace: 'nowrap',
          }}
        >
          TroopHerald
        </span>
      )}
    </span>
  );
}
